import {
  Box,
  Container,
  HStack,
  Input,
  SimpleGrid,
  Switch,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Icon from 'components/Icon';
import { ProtectedElement } from 'components/ProtectedElement';
import { SEO } from 'components/SEO';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { ERoute } from 'types/frontend';

import PDFUploadTab from './components/PDFUploadTab';
import { CVTemplate, ROLE_SUGGESTIONS } from './types';

type NewCVTab = 'generate' | 'upload';

const TEMPLATE_OPTIONS: { value: CVTemplate; label: string; description: string }[] = [
  {
    value: 'classic',
    label: 'Classic',
    description: 'Single column, traditional layout. Maximum ATS compatibility.',
  },
  {
    value: 'modern',
    label: 'Modern',
    description: 'Two-column layout with sidebar. Clean and professional.',
  },
  {
    value: 'creative',
    label: 'Creative',
    description: 'Accent blocks and visual flair. Stands out while staying ATS-safe.',
  },
];

const NewCV = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { isMobile, isDesktopNavigation } = useMediaQueries();

  const [activeTab, setActiveTab] = useState<NewCVTab>('generate');
  const [roleInput, setRoleInput] = useState(ROLE_SUGGESTIONS[0]);
  const [selectedTemplate, setSelectedTemplate] = useState<CVTemplate>('classic');
  const [fillFromBDS, setFillFromBDS] = useState(true);

  const handleRolePillClick = (role: string) => {
    setRoleInput(role);
  };

  const handleCreate = () => {
    if (!roleInput.trim()) return;

    navigate(`${ERoute.CVBuilder}${ERoute.GeneratingCV}`, {
      state: {
        mode: 'generate',
        role: roleInput.trim(),
        template: selectedTemplate,
        fillFromBDS,
      },
    });
  };

  return (
    <VStack spacing={0} alignItems="stretch" minHeight="calc(100vh - 60px - 32px)">
      {/* Back button — pinned to left of content area */}
      <HStack
        mb={2}
        mt={2}
        ml={4}
        cursor="pointer"
        onClick={() => navigate(ERoute.CVBuilder)}
        _hover={{ opacity: 0.8 }}
        transition="opacity 0.15s"
        role="button"
        tabIndex={0}
        width="fit-content"
        onKeyDown={(e) => {
          if (e.key === 'Enter') navigate(ERoute.CVBuilder);
        }}
      >
        <Icon type="BackArrow" />
        <Text fontSize="small" opacity="secondary">
          Back to CV Builder
        </Text>
      </HStack>

      <Container maxW="900px" width="100%" flex={1} display="flex" flexDirection="column">
        <SEO
          title="BigDevSoon | Create CV"
          description="Create a new professional CV with AI-powered content generation."
          url={`${process.env.REACT_APP_SITE_URL}cv-builder/new`}
        />

        {/* Tab Switcher */}
      <HStack spacing={0} mb={6}>
        {(['generate', 'upload'] as const).map((tab) => {
          const isActive = activeTab === tab;
          const label = tab === 'generate' ? 'Create with AI' : 'Upload PDF';

          return (
            <Box
              key={tab}
              as="button"
              flex={1}
              py={3}
              textAlign="center"
              borderBottom="2px solid"
              borderColor={isActive ? 'purple.400' : 'whiteAlpha.200'}
              color={isActive ? 'white' : 'whiteAlpha.500'}
              fontWeight={isActive ? '600' : '400'}
              fontSize="14px"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ color: 'white', borderColor: isActive ? 'purple.400' : 'whiteAlpha.400' }}
              onClick={() => setActiveTab(tab)}
            >
              {label}
            </Box>
          );
        })}
      </HStack>

      {activeTab === 'generate' ? (
        <VStack spacing={8} alignItems="stretch">
          {/* Header */}
          <VStack spacing={2} textAlign="center">
            <Text fontSize="xxxLarge" fontWeight="bold">
              Create a New CV
            </Text>
            <Text fontSize="medium" opacity="secondary">
              Tell us the role you&apos;re targeting and AI will generate
              <br />
              an optimized CV structure with the best keywords and sections.
            </Text>
          </VStack>

          {/* Role Input */}
          <Box
            backgroundColor="whiteAlpha.50"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.100"
            p={6}
          >
            <VStack spacing={4} alignItems="stretch">
              <Text fontSize="large" fontWeight="semibold">
                What role are you applying for?
              </Text>
              <Input
                value={roleInput}
                onChange={(e) => setRoleInput(e.target.value)}
                placeholder="e.g. Senior Frontend Developer"
                size="lg"
                variant="filled"
                autoFocus
                maxLength={100}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && roleInput.trim()) {
                    handleCreate();
                  }
                }}
              />
              <Wrap spacing={2.5}>
                {ROLE_SUGGESTIONS.map((role) => {
                  const isSelected = roleInput === role;

                  return (
                    <WrapItem key={role}>
                      <Box
                        as="button"
                        px={3.5}
                        py={1.5}
                        borderRadius="full"
                        border="1px solid"
                        borderColor={isSelected ? 'purple.400' : 'whiteAlpha.300'}
                        backgroundColor={isSelected ? 'purple.500' : 'whiteAlpha.100'}
                        color={isSelected ? 'white' : 'whiteAlpha.800'}
                        fontSize="13px"
                        fontWeight="500"
                        cursor="pointer"
                        transition="all 0.15s"
                        _hover={{
                          borderColor: isSelected ? 'purple.300' : 'purple.400',
                          backgroundColor: isSelected ? 'purple.400' : 'whiteAlpha.200',
                          color: 'white',
                        }}
                        onClick={() => handleRolePillClick(role)}
                      >
                        {role}
                      </Box>
                    </WrapItem>
                  );
                })}
              </Wrap>
            </VStack>
          </Box>

          {/* Template Selector */}
          <Box
            backgroundColor="whiteAlpha.50"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.100"
            p={6}
          >
            <Text fontSize="large" fontWeight="semibold" mb={4}>
              Choose a template
            </Text>
            <SimpleGrid columns={isDesktopNavigation ? 3 : 1} gap={4}>
              {TEMPLATE_OPTIONS.map((template) => (
                <Box
                  key={template.value}
                  p={4}
                  borderRadius="lg"
                  border="2px solid"
                  borderColor={
                    selectedTemplate === template.value ? 'purple.400' : 'whiteAlpha.200'
                  }
                  backgroundColor={
                    selectedTemplate === template.value ? 'whiteAlpha.100' : 'transparent'
                  }
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ borderColor: 'whiteAlpha.400' }}
                  onClick={() => setSelectedTemplate(template.value)}
                >
                  {/* Mini template preview */}
                  <Box
                    backgroundColor="white"
                    borderRadius="md"
                    p={3}
                    mb={3}
                    height={isMobile ? '80px' : '100px'}
                    overflow="hidden"
                  >
                    {template.value === 'classic' && (
                      <>
                        <Box height="3px" width="50%" backgroundColor="gray.400" borderRadius="full" mb={2} mx="auto" />
                        <Box height="2px" width="30%" backgroundColor="gray.300" borderRadius="full" mb={3} mx="auto" />
                        <Box height="2px" width="80%" backgroundColor="gray.200" borderRadius="full" mb={1} />
                        <Box height="2px" width="70%" backgroundColor="gray.200" borderRadius="full" mb={1} />
                        <Box height="2px" width="75%" backgroundColor="gray.200" borderRadius="full" />
                      </>
                    )}
                    {template.value === 'modern' && (
                      <HStack spacing={2} height="100%">
                        <Box width="35%" height="100%" backgroundColor="gray.200" borderRadius="sm" p={1}>
                          <Box height="2px" width="80%" backgroundColor="gray.400" borderRadius="full" mb={1} />
                          <Box height="1px" width="60%" backgroundColor="gray.300" borderRadius="full" mb={1} />
                          <Box height="1px" width="70%" backgroundColor="gray.300" borderRadius="full" />
                        </Box>
                        <Box flex={1}>
                          <Box height="3px" width="60%" backgroundColor="gray.400" borderRadius="full" mb={2} />
                          <Box height="2px" width="90%" backgroundColor="gray.200" borderRadius="full" mb={1} />
                          <Box height="2px" width="80%" backgroundColor="gray.200" borderRadius="full" mb={1} />
                          <Box height="2px" width="85%" backgroundColor="gray.200" borderRadius="full" />
                        </Box>
                      </HStack>
                    )}
                    {template.value === 'creative' && (
                      <>
                        <Box height="4px" width="100%" backgroundColor="purple.200" borderRadius="full" mb={2} />
                        <Box height="3px" width="40%" backgroundColor="gray.400" borderRadius="full" mb={2} />
                        <Box height="3px" width="100%" backgroundColor="purple.100" borderRadius="full" mb={2} />
                        <Box height="2px" width="80%" backgroundColor="gray.200" borderRadius="full" mb={1} />
                        <Box height="2px" width="70%" backgroundColor="gray.200" borderRadius="full" />
                      </>
                    )}
                  </Box>
                  <Text fontSize="medium" fontWeight="semibold">
                    {template.label}
                  </Text>
                  <Text fontSize="small" opacity="secondary">
                    {template.description}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          {/* BDS Data Fill Toggle */}
          <Box
            backgroundColor="whiteAlpha.50"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.100"
            p={6}
          >
            <HStack justifyContent="space-between">
              <VStack alignItems="flex-start" spacing={1}>
                <Text fontSize="medium" fontWeight="semibold">
                  Fill from your BDS profile
                </Text>
                <Text fontSize="small" opacity="secondary">
                  Pre-fill personal info, projects, and achievements from your BigDevSoon profile.
                </Text>
              </VStack>
              <Switch
                isChecked={fillFromBDS}
                onChange={(e) => setFillFromBDS(e.target.checked)}
                colorScheme="purple"
                size="lg"
              />
            </HStack>
          </Box>

        </VStack>
      ) : (
        <VStack spacing={8} alignItems="stretch" flex={1}>
          {/* Header */}
          <VStack spacing={2} textAlign="center">
            <Text fontSize="xxxLarge" fontWeight="bold">
              Import from PDF
            </Text>
            <Text fontSize="medium" opacity="secondary">
              Upload an existing CV in PDF format. AI will extract the content and structure it into
              an editable template.
            </Text>
          </VStack>

          <PDFUploadTab />
        </VStack>
      )}

    </Container>

      {/* Spacer pushes bottom bar down */}
      <Box flex={1} />

      {/* Bottom action bar — full width, docked to bottom */}
      {activeTab === 'generate' && (
        <Box
          width="calc(100% + 32px)"
          ml={-4}
          py={3}
          mb="-60px"
          display="flex"
          justifyContent="center"
          borderTop="1px solid"
          borderColor="whiteAlpha.100"
          backgroundColor="gray.900"
          position="sticky"
          bottom="-60px"
          zIndex={10}
        >
          <ProtectedElement
            scheme="gradient"
            size="lg"
            isDisabled={!roleInput.trim()}
            onClick={handleCreate}
            requiredTokens={3}
          >
            Generate CV
          </ProtectedElement>
        </Box>
      )}
    </VStack>
  );
};

export default NewCV;
